import { KdsSpinner } from "@kazaar-platform/design-system";
import { type ComponentType, createElement, lazy, Suspense } from "react";
import { isChunkLoadError } from "@/helpers/isChunkLoadError";

// ============================================================================
// Page Fallback Component
// ============================================================================
export const PageFallback = () =>
  createElement(
    "div",
    { className: "flex h-full items-center justify-center" },
    createElement(KdsSpinner, { size: "lg" })
  );

/**
 * Wraps a dynamic import and handles chunk load errors caused by a new
 * deployment (the old hashed filename no longer exists on the server).
 * When detected, forces a full page reload so the browser fetches the new
 * index.html (which references the new chunk filenames).
 * A sessionStorage guard prevents infinite reload loops.
 */
const RELOAD_STORAGE_KEY = "chunk-error-reload";

const retryImport = <M>(importFn: () => Promise<M>): Promise<M> =>
  importFn().catch((error: unknown) => {
    if (isChunkLoadError(error)) {
      const lastReload = sessionStorage.getItem(RELOAD_STORAGE_KEY);
      const now = Date.now();

      // Only auto-reload once every 10 seconds to prevent infinite loops
      if (!lastReload || now - Number(lastReload) > 10_000) {
        sessionStorage.setItem(RELOAD_STORAGE_KEY, String(now));
        window.location.reload();
        // Return a never-resolving promise to avoid rendering while reloading
        return new Promise<M>(() => {});
      }
    }
    throw error;
  });

/**
 * Creates a lazy-loaded page component with Suspense wrapper built-in.
 * Includes automatic retry on chunk load errors (e.g. after a Vite rebuild).
 * Usage: const MyPage = lazyPage(() => import("./MyPage"), (m) => m.MyPage);
 */
export const lazyPage = <M, C extends ComponentType>(
  importFn: () => Promise<M>,
  resolver: (module: M) => C
) => {
  const LazyComponent = lazy(() =>
    retryImport(importFn).then((m) => ({
      default: resolver(m) as ComponentType<object>,
    }))
  );

  return () =>
    createElement(
      Suspense,
      { fallback: createElement(PageFallback) },
      createElement(LazyComponent)
    );
};

// ============================================================================
// Lazy loaded pages - Client
// ============================================================================
export const AccountPage = lazyPage(
  () => import("@/pages/AccountPage"),
  (m) => m.AccountPage
);
export const CatalogMediumPage = lazyPage(
  () => import("@/pages/CatalogMediumPage"),
  (m) => m.CatalogMediumPage
);
export const CatalogPage = lazyPage(
  () => import("@/pages/CatalogPage"),
  (m) => m.CatalogPage
);
export const InboxPage = lazyPage(
  () => import("@/pages/InboxPage/InboxPage.tsx"),
  (m) => m.InboxPage
);
export const OrderPage = lazyPage(
  () => import("@/pages/OrderPage"),
  (m) => m.OrderPage
);
export const OrdersPage = lazyPage(
  () => import("@/pages/OrdersPage"),
  (m) => m.OrdersPage
);
export const OrderSuccessPage = lazyPage(
  () => import("@/pages/OrderSuccessPage"),
  (m) => m.OrderSuccessPage
);
export const DashboardPage = lazyPage(
  () => import("@/pages/DashboardPage"),
  (m) => m.DashboardPage
);
export const ModificationRequestPage = lazyPage(
  () => import("@/pages/ModificationRequestPage"),
  (m) => m.ModificationRequestPage
);

export const OrderItemPage = lazyPage(
  () => import("@/pages/OrderItemPage"),
  (m) => m.OrderItemPage
);

// ============================================================================
// Lazy loaded pages - Auth
// ============================================================================
export const ForgotPasswordPage = lazyPage(
  () => import("@/pages/Auth/ForgotPasswordPage/ForgotPasswordPage.tsx"),
  (m) => m.ForgotPasswordPage
);
export const ResetPasswordPage = lazyPage(
  () => import("@/pages/Auth/ResetPasswordPage/ResetPasswordPage.tsx"),
  (m) => m.ResetPasswordPage
);
export const SignInPage = lazyPage(
  () => import("@/pages/Auth/SignInPage/SignInPage.tsx"),
  (m) => m.SignInPage
);
export const SSOLoginPage = lazyPage(
  () => import("@/pages/Auth/SSOLoginPage/SSOLoginPage.tsx"),
  (m) => m.SSOLoginPage
);

// ============================================================================
// Lazy loaded pages - OMS
// ============================================================================
export const RetroplanningFormPage = lazyPage(
  () => import("@/pages/RetroplanningFormPage/RetroplanningFormPage.tsx"),
  (m) => m.RetroplanningFormPage
);
export const EcoScoringFormPage = lazyPage(
  () => import("@/pages/EcoScoringFormPage/"),
  (m) => m.EcoScoringFormPage
);
export const OrderDetailsAddQuoteFormPage = lazyPage(
  () => import("@/pages/OrderDetailsAddQuoteFormPage"),
  (m) => m.OrderDetailsAddQuoteFormPage
);
export const SettingsFormPage = lazyPage(
  () => import("@/pages/SettingsFormPage"),
  (m) => m.SettingsFormPage
);

// ============================================================================
// Lazy loaded pages - Admin
// ============================================================================
export const AdminRedirectPage = lazyPage(
  () => import("@/pages/Admin/AdminRedirectPage"),
  (m) => m.AdminRedirectPage
);
export const AdminAccountPage = lazyPage(
  () => import("@/pages/Admin/AccountPage"),
  (m) => m.AccountPage
);
export const AdminOrganizationPage = lazyPage(
  () => import("@/pages/Admin/OrganizationPage"),
  (m) => m.OrganizationPage
);
export const AdminWorkspacePage = lazyPage(
  () => import("@/pages/Admin/WorkspacePage"),
  (m) => m.WorkspacePage
);
